import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { _api_post_add_document } from "../api/index.hr";
import { useToastGlobal } from "../../../stores/useToastGlobal";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import { queryClient } from "../../..";

/**
 * @typedef {Object} DropzoneState
 * @property {Array} files - Array of files in the dropzone.
 * @property {Array} filesUploaded - Array of files uploaded successfully to back.
 * @property {Object} progress - Object representing the progress of file uploads.
 * @property {Function} setFiles - Function to set the files in the dropzone.
 * @property {Function} setFilesUploaded - Function to set the files Uploaded Successfully to back.
 * @property {Function} setProgress - Function to set the progress of file uploads.
 * @property {Function} removeFile - Function to remove a file from the dropzone.
 * @property {Function} resetFiles - Function to reset the files STATES in the dropzone.
 */

/**
 * @typedef {Object} UseLogicDialogAddDocumentReturn
 * @property {boolean} open - State indicating whether the dialog is open.
 * @property {Function} setOpen - Function to set the open state.
 * @property {Function} closeDialog - Function to close the dialog.
 * @property {Function} openDialog - Function to open the dialog.
 * @property {DropzoneState} dz - Dropzone related state and functions.
 * @property {Object} methods - Methods provided by react-hook-form.
 * @property {Object} mutation - Functions to be used in the form.
 * @property {Object} form - Form related state.
 */

/**
 * Custom hook that provides logic for handling the dialog to add a document.
 *
 * @returns {UseLogicDialogAddDocumentReturn} Hook return object.
 */
const useLogicDialogAddDocument = ({
    onSuccess,
    showActions = true,
    showAdditionalInformation = true,
    queryKeyToRefesh = null,
}) => {
    const [open, setOpen] = React.useState(false);
    const [files, setFiles] = React.useState([]);
    const [filesUploaded, setFilesUploaded] = React.useState([]);
    const [progress, setProgress] = React.useState({});

    const globalToast = useToastGlobal();
    const navigate = useNavigate();

    const methods = useForm();

    const { mutate: mutateAddDocument, isLoading } = useMutation(
        (data) => _api_post_add_document(data),
        {
            onSuccess: (response) => {
                if (response?.success) {
                    globalToast.setShow({
                        visible: true,
                        content: "Document(s) ajouté(s)",
                        level: "success",
                        auto_hide_timeout: 5000,
                        dismissible: true,
                        actions: showActions
                            ? [
                                  {
                                      content: "Aller au dossier",
                                      size: "small",
                                      key: "goto_documents",
                                      flat: true,
                                      styles: {
                                          marginLeft: "250px",
                                      },
                                      onClick: () => {
                                          if (showAdditionalInformation) {
                                              return navigate(
                                                  `${ROUTES.document_hr.society}?f=${response.redirect_folder}`
                                              );
                                          } else {
                                              queryClient.invalidateQueries(
                                                  queryKeyToRefesh
                                              );
                                              return;
                                          }
                                      },
                                  },
                              ]
                            : null,
                    });
                } else {
                    globalToast.setShow({
                        visible: true,
                        content:
                            "Une erreur est survenue lors de l'ajout des documents",
                        level: "danger",
                    });
                }
                closeDialog();
                onSuccess();
                return;
            },
        }
    );

    const func_remove_file = (document_name) => {
        setFiles((prevFiles) => {
            return prevFiles.filter(
                (prevFile) => prevFile.name !== document_name
            );
        });

        setFilesUploaded((prevFilesUploaded) => {
            return prevFilesUploaded.filter(
                (prevFileUploaded) =>
                    prevFileUploaded.original_filename !== document_name
            );
        });
    };

    const func_reset_files = () => {
        setFiles([]);
        setFilesUploaded([]);
        setProgress({});
    };

    const closeDialog = () => {
        setOpen(false);
        func_reset_files();
        methods.reset();
        return;
    };

    const func_disable_confirm_button = useCallback(() => {
        let disabled = false;
        if (showAdditionalInformation === true) {
            disabled =
                files.length === 0 ||
                filesUploaded.length === 0 ||
                methods.formState.isValid === false;
        } else {
            disabled = files.length === 0 && filesUploaded.length === 0;
        }
        return disabled;
    }, [files, filesUploaded, methods.formState.isValid]);

    return {
        open,
        setOpen,
        closeDialog,
        openDialog: () => setOpen(true),
        dz: {
            files,
            filesUploaded,
            setFilesUploaded,
            progress,
            setFiles,
            setProgress,
            removeFile: func_remove_file,
            resetFiles: func_reset_files,
        },
        form: {
            disable_confirm_button: func_disable_confirm_button(),
        },
        methods,
        mutation: {
            isLoading,
            onSubmit: (data) => {
                const document_ids = filesUploaded.map((file) => file.id);
                const folder = showAdditionalInformation
                    ? data.category.key
                    : "common";
                const user_id = showAdditionalInformation
                    ? data.user.key?.replaceAll("u-", "")
                    : null;
                return mutateAddDocument({
                    document_ids,
                    folder,
                    user_id,
                });
            },
        },
    };
};

export default useLogicDialogAddDocument;
